import React from "react";
import dayjs from "dayjs";
import { Button, DatePicker, Input } from "antd";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiAirplaneLanding, mdiAirplaneTakeoff, mdiCalendarMonth } from "@mdi/js";
import { LandTourCategory, LandTourDetail } from "../../../../interfaces/land-tour.interface";
import ChooseYourTour, { DateSelection, PaxCounts } from "../components/ChooseYourTour";
import { FlightDetails, INVALID_ROOM_MESSAGE, RoomOption, defaultReturnDate, travellersSummary } from "./bookingUtils";
import BookingFooter from "./BookingFooter";
import { FALLBACK_IMAGE } from "../components/GroundTourGallery";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { MobileFooter, MobileProgressHeader, MobileTitleHeader } from "./MobileChrome";

const dateFormat = "DD MMM YYYY";
// Read-only dates (flight included) are written out in full, e.g. "11 August 2026" (Figma 10277:303511).
const longDateFormat = "DD MMMM YYYY";

export interface SelectionErrors {
  room?: boolean;
  language?: boolean;
  departureDate?: boolean;
}

interface Props {
  /** "customize" follows Select Tour/Next; "edit" is reached from the Edit buttons and also lets the customer change category, date and pax. */
  mode: "customize" | "edit";
  landTour: LandTourDetail;
  category: LandTourCategory;
  selectedDate: DateSelection;
  paxCounts: PaxCounts;
  onCategoryChange: (category: LandTourCategory) => void;
  onDateChange: (selection: DateSelection) => void;
  /** Edit Selection: the chosen date no longer applies to the new category. */
  dateCleared?: boolean;
  onPaxCountsChange: (counts: PaxCounts) => void;
  roomOptions: RoomOption[];
  room: string;
  onRoomChange: (label: string) => void;
  language: string;
  onLanguageChange: (language: string) => void;
  flights: FlightDetails;
  onFlightsChange: (flights: FlightDetails) => void;
  errors: SelectionErrors;
  total: number | undefined;
  totalLoading: boolean;
  onShowBreakdown: () => void;
  onBack: () => void;
  onCancel: () => void;
  onContinue: () => void;
}

function RadioCards({ name, options, value, onChange }: {
  name: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="gt-radio-grid" role="radiogroup" aria-label={name}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          className={`gt-radio-card ${value === option.value ? "is-selected" : ""}`}
          onClick={() => onChange(option.value)}
        >
          <span>{option.label}</span>
          <i className="gt-radio-card__dot" />
        </button>
      ))}
    </div>
  );
}

function FlightRow({ kind, dateLabel, dateRequired, disabled, dateValue, onDateChange, route, number, time, onRoute, onNumber, onTime, minDate }: {
  kind: "departure" | "return";
  dateLabel: string;
  dateRequired?: boolean;
  disabled?: boolean;
  dateValue: string;
  onDateChange?: (value: string) => void;
  route: string;
  number: string;
  time: string;
  onRoute?: (value: string) => void;
  onNumber?: (value: string) => void;
  onTime?: (value: string) => void;
  minDate?: string;
}) {
  const isMobile = useBreakpoint();
  const placeholder = (text: string) => (disabled ? "To be confirmed" : text);
  return (
    <div className="gt-flight">
      <span className={`gt-flight__tag is-${kind}`}>
        <Icon path={kind === "departure" ? mdiAirplaneTakeoff : mdiAirplaneLanding} size="16px" />
        Flight {kind === "departure" ? "Departure" : "Return"} Details
      </span>
      <div className="gt-flight__fields">
        <label>
          <span>{dateLabel}{dateRequired && <b>*</b>}</span>
          <DatePicker
            value={dateValue ? dayjs(dateValue) : null}
            format={disabled ? longDateFormat : dateFormat}
            placeholder="Please Select"
            disabled={disabled}
            suffixIcon={<Icon path={mdiCalendarMonth} size="20px" color="#000" />}
            disabledDate={minDate ? (current) => current.isBefore(dayjs(minDate), "day") : undefined}
            onChange={(value) => onDateChange?.(value ? value.format("YYYY-MM-DD") : "")}
            status={dateRequired && !dateValue ? "error" : undefined}
          />
        </label>
        <label>
          <span>Flight Route</span>
          <Input value={route} disabled={disabled} placeholder={placeholder("e.g. SIN-KUL")} onChange={(event) => onRoute?.(event.target.value)} />
        </label>
        <label>
          <span>Flight Number</span>
          <Input value={number} disabled={disabled} placeholder={placeholder(isMobile ? "e.g. MH 611" : "e.g. D7 673")} onChange={(event) => onNumber?.(event.target.value)} />
        </label>
        <label>
          <span>Departure Time</span>
          <Input value={time} disabled={disabled} placeholder={placeholder("e.g. 1030 - 1130")} onChange={(event) => onTime?.(event.target.value)} />
        </label>
      </div>
    </div>
  );
}

export default function SelectionPage(props: Props) {
  const {
    mode, landTour, category, selectedDate, paxCounts, roomOptions, room, language, flights, errors,
  } = props;
  const isMobile = useBreakpoint();
  const set = (patch: Partial<FlightDetails>) => props.onFlightsChange({ ...flights, ...patch });
  const image = landTour.coverImage || landTour.images?.[0] || FALLBACK_IMAGE;
  const languages = landTour.languages || [];

  const roomSection = (
    <section className="gt-card">
      <h3 className="gt-card__title">Room Selections</h3>
      <p className="gt-note">Room type is subject to hotel availability.</p>
      <RadioCards
        name="Room selection"
        options={roomOptions.map((option) => ({ value: option.label, label: option.display }))}
        value={room}
        onChange={props.onRoomChange}
      />
      {errors.room && <p className="gt-field-error">Please select a room type.</p>}
    </section>
  );

  const languageSection = (
    <section className="gt-card">
      <h3 className="gt-card__title">Tour Language</h3>
      <RadioCards
        name="Tour language"
        options={languages.map((value) => ({ value, label: value }))}
        value={language}
        onChange={props.onLanguageChange}
      />
      {errors.language && <p className="gt-field-error">Please select a tour language.</p>}
    </section>
  );

  const flightSection = landTour.incFlight ? (
    <section className="gt-card">
      <h3 className="gt-card__title">Flights Included in Your Tour</h3>
      <p className="gt-note">Your return flights are included. Flight route and number will be confirmed closer to departure.</p>
      {/* Dates follow the departure date (and the duration for the return); the rest comes from the tour's own flight setting. */}
      <FlightRow
        kind="departure" dateLabel="Flight Departure Date" disabled
        dateValue={selectedDate.date}
        route={landTour.flightDepartOriginDestination || ""}
        number={landTour.flightDepartureNo || ""}
        time={landTour.flightDepartArrivalTime || ""}
      />
      <FlightRow
        kind="return" dateLabel="Flight Return Date" disabled
        dateValue={defaultReturnDate(selectedDate.date, landTour)}
        route={landTour.flightReturnOriginDestination || ""}
        number={landTour.flightReturnNo || ""}
        time={landTour.flightReturnArrivalTime || ""}
      />
    </section>
  ) : (
    <section className="gt-card">
      <h3 className="gt-card__title">Please provide your own flight details</h3>
      <FlightRow
        kind="departure" dateLabel="Flight Departure Date" dateRequired
        dateValue={flights.flight_departure_date}
        onDateChange={(value) => set({ flight_departure_date: value })}
        route={flights.depart_origin_destination} onRoute={(value) => set({ depart_origin_destination: value })}
        number={flights.departure_flight_no} onNumber={(value) => set({ departure_flight_no: value })}
        time={flights.depart_dept_arrival_time} onTime={(value) => set({ depart_dept_arrival_time: value })}
      />
      {errors.departureDate && <p className="gt-field-error">Flight departure date is required.</p>}
      <FlightRow
        kind="return" dateLabel="Flight Return Date"
        dateValue={flights.flight_return_date}
        minDate={flights.flight_departure_date || undefined}
        onDateChange={(value) => set({ flight_return_date: value })}
        route={flights.return_origin_destination} onRoute={(value) => set({ return_origin_destination: value })}
        number={flights.return_flight_no} onNumber={(value) => set({ return_flight_no: value })}
        time={flights.return_departure_arrv_time} onTime={(value) => set({ return_departure_arrv_time: value })}
      />
    </section>
  );

  const editSection = (
    <section className="gt-card gt-embed-card">
      <div id="ground-tour-show-v2" className="gt-embed">
        <ChooseYourTour
          landTour={landTour}
          selectedCategory={category}
          onCategoryChange={props.onCategoryChange}
          selectedDate={props.dateCleared ? undefined : selectedDate}
          dateError={props.dateCleared}
          travellersError={roomOptions.length === 0 ? INVALID_ROOM_MESSAGE : undefined}
          onDateChange={props.onDateChange}
          paxCounts={paxCounts}
          onPaxCountsChange={props.onPaxCountsChange}
          categoryHeading={isMobile ? "Tour Category Selection" : "Tour Category"}
          dateHeading={isMobile ? "Select Date" : "Departure Date"}
          travellersHeading={isMobile ? "Travellers" : "Pax Selection"}
          viewAllBelow={isMobile}
        />
      </div>
    </section>
  );

  if (isMobile) {
    return (
      <div className="gt-page gt-m gt-selection">
        {mode === "edit"
          ? <MobileTitleHeader title="Edit Selection" onClose={props.onCancel} />
          : <MobileProgressHeader step={1} onBack={props.onBack} />}

        <div className="gt-m-body">
          {mode === "customize" ? (
            <>
              <h2 className="gt-m-title">Please fill up details below</h2>
              <section className="gt-card gt-m-tour">
                <div className="gt-m-tour__top">
                  {image && <img src={image} alt={landTour.title} />}
                  <h3>{category.category}</h3>
                </div>
                <span className="gt-muted">Departure Date</span>
                <strong>{dayjs(selectedDate.date).format("DD MMM YYYY")}</strong>
                <span className="gt-muted">Travellers</span>
                <strong>{travellersSummary(paxCounts)}</strong>
              </section>
            </>
          ) : editSection}
          {roomSection}
          {languageSection}
          {flightSection}
        </div>

        {mode === "edit" ? (
          <MobileFooter total={props.total} loading={props.totalLoading} onShowBreakdown={props.onShowBreakdown}>
            <Button type="primary" size="large" onClick={props.onContinue}>Done</Button>
          </MobileFooter>
        ) : (
          <MobileFooter stacked total={props.total} loading={props.totalLoading} onShowBreakdown={props.onShowBreakdown}>
            <Button type="primary" size="large" block onClick={props.onContinue}>Continue</Button>
          </MobileFooter>
        )}
      </div>
    );
  }

  return (
    <div className="gt-page gt-selection">
      <div className="gt-container">
        <div className="gt-topbar">
          <button type="button" className="gt-back-link" onClick={props.onBack}>
            <Icon path={mdiArrowLeft} size="20px" /> Back
          </button>
          {mode === "edit" && <h1>Edit Selection</h1>}
        </div>

        {mode === "customize" ? (
          <section className="gt-card gt-tour-card">
            {image && <img src={image} alt={landTour.title} />}
            <div>
              <h2>{category.category}</h2>
              <span className="gt-muted">Departure Date</span>
              <strong>{dayjs(selectedDate.date).format(dateFormat)}</strong>
              <span className="gt-muted">Travellers</span>
              <strong>{travellersSummary(paxCounts)}</strong>
            </div>
          </section>
        ) : editSection}

        {roomSection}
        {languageSection}
        {flightSection}
      </div>

      <BookingFooter total={props.total} loading={props.totalLoading} onShowBreakdown={props.onShowBreakdown}>
        <Button size="large" onClick={props.onCancel}>Cancel</Button>
        <Button size="large" type="primary" onClick={props.onContinue}>{mode === "edit" ? "Save" : "Continue"}</Button>
      </BookingFooter>
    </div>
  );
}
